import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Upload, FileSpreadsheet, FileText, AlertCircle, X } from 'lucide-react';
import { reconApi } from '@/shared/api';

/** Single-file upload zone with optional remove chip */
function SingleFileZone({
  label, hint, accept, icon: Icon, file, onChange, optional,
}: {
  label: string;
  hint: string;
  accept: string;
  icon: React.ElementType;
  file: File | null;
  onChange: (f: File | null) => void;
  optional?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-axis-ink flex items-center gap-1.5">
        {label}
        {optional && <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-axis-cloud-100 text-axis-ash border border-axis-cloud-200">optional</span>}
      </span>
      {!file ? (
        <label className="flex items-center gap-3 p-4 border-2 border-dashed border-axis-cloud-200 rounded-md hover:border-axis-maroon cursor-pointer h-[72px]">
          <Icon className="w-5 h-5 text-axis-maroon shrink-0" />
          <span className="text-xs text-axis-ash leading-snug">{hint}</span>
          <input type="file" accept={accept} className="hidden"
            onChange={e => onChange(e.target.files?.[0] ?? null)} />
        </label>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 bg-axis-cloud-50 border border-axis-cloud-200 rounded-md h-[72px]">
          <Icon className="w-4 h-4 text-axis-maroon shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate">{file.name}</div>
            <div className="text-[10px] text-axis-ash">{(file.size / 1024).toFixed(1)} KB</div>
          </div>
          <button type="button" onClick={() => onChange(null)}
            className="shrink-0 p-0.5 rounded-full hover:bg-axis-cloud-200 text-axis-ash hover:text-rose-500 transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

/** Multi-file upload zone */
function MultiFileZone({
  label, hint, accept, icon: Icon, files, onChange, max, required,
}: {
  label: string;
  hint: (count: number) => string;
  accept: string;
  icon: React.ElementType;
  files: File[];
  onChange: (files: File[]) => void;
  max: number;
  required?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-axis-ink flex items-center gap-1.5">
        {label}
        {required && <span className="text-[10px] font-normal px-1.5 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-200">required</span>}
      </span>
      {files.length < max && (
        <label className="flex items-center gap-3 p-4 border-2 border-dashed border-axis-cloud-200 rounded-md hover:border-axis-maroon cursor-pointer h-[72px]">
          <Icon className="w-5 h-5 text-axis-maroon shrink-0" />
          <span className="text-xs text-axis-ash leading-snug">{hint(files.length)}</span>
          <input type="file" multiple accept={accept} className="hidden"
            onChange={e => {
              const inc = Array.from(e.target.files ?? []);
              onChange([...files, ...inc].slice(0, max));
            }} />
        </label>
      )}
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 bg-axis-cloud-50 border border-axis-cloud-200 rounded-full max-w-[200px]">
              <Icon className="w-3.5 h-3.5 text-axis-maroon shrink-0" />
              <div className="min-w-0">
                <div className="text-xs font-medium truncate">{f.name}</div>
                <div className="text-[10px] text-axis-ash">{(f.size / 1024).toFixed(1)} KB</div>
              </div>
              <button type="button"
                onClick={() => onChange(files.filter((_, idx) => idx !== i))}
                className="ml-0.5 shrink-0 rounded-full hover:bg-axis-cloud-200 text-axis-ash hover:text-rose-500 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {files.length === max && (
            <div className="text-[11px] text-axis-ash self-center">Maximum of {max} files reached.</div>
          )}
        </div>
      )}
    </div>
  );
}

export function NewReconJobPage() {
  const nav = useNavigate();
  const [portfolioFiles, setPortfolioFiles] = useState<File[]>([]);
  const [pfHoldingFile, setPfHoldingFile] = useState<File | null>(null);
  const [dpFiles, setDpFiles] = useState<File[]>([]);
  const [dpHoldingFile, setDpHoldingFile] = useState<File | null>(null);
  const [portfolioAsOn, setPortfolioAsOn] = useState('');
  const [dpAsOn, setDpAsOn] = useState('');

  const mutation = useMutation({
    mutationFn: reconApi.create,
    onSuccess: (data) => nav(`/recon/${data.job_id}`),
  });

  const ready = portfolioFiles.length > 0 && dpFiles.length > 0;
  const policyWarning = !!portfolioAsOn && !!dpAsOn &&
    Math.abs(new Date(portfolioAsOn).getTime() - new Date(dpAsOn).getTime()) / 86_400_000 > 7;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <h1 className="text-2xl font-semibold text-axis-ink mb-1">New reconciliation</h1>
      <p className="text-sm text-axis-ash mb-6">
        Upload a portfolio transaction file and one or more DP transaction statements. The pipeline parses, normalizes ISINs,
        applies known corporate actions, and classifies each mismatch with Claude Opus 4.7.
      </p>

      <div className="card p-6 space-y-6">

        {/* ── Row 1: PF Transaction (multi) | PF Holding (single) ── */}
        <div className="grid grid-cols-2 gap-4">
          <MultiFileZone
            label="Portfolio Transaction file(s) (.xlsx) — up to 4 files"
            hint={n => n === 0 ? 'Click to select up to 4 .xlsx files' : `Add more files (${n}/4 selected)`}
            accept=".xlsx,.xls"
            icon={FileSpreadsheet}
            files={portfolioFiles}
            onChange={setPortfolioFiles}
            max={4}
            required
          />
          <SingleFileZone
            label="Portfolio Holding file (.xlsx / .csv)"
            hint="Click to select one file (optional — needed for Holding Recon)"
            accept=".xlsx,.xls,.csv"
            icon={FileSpreadsheet}
            file={pfHoldingFile}
            onChange={setPfHoldingFile}
            optional
          />
        </div>

        {/* ── Row 2: DP Transaction (multi) | DP Holding (single) ── */}
        <div className="grid grid-cols-2 gap-4">
          <MultiFileZone
            label="DP transaction statement(s) (.pdf / .xlsx / .csv)"
            hint={n => n === 0 ? 'Click to select one or more files (PDF, CSV, or Excel)' : `Add more DP files (${n} selected)`}
            accept=".pdf,.csv,.xlsx,.xls"
            icon={FileText}
            files={dpFiles}
            onChange={setDpFiles}
            max={99}
            required
          />
          <SingleFileZone
            label="DP Holding file (.xlsx / .csv)"
            hint="Click to select one file (optional — needed for Holding Recon)"
            accept=".xlsx,.xls,.csv"
            icon={FileText}
            file={dpHoldingFile}
            onChange={setDpHoldingFile}
            optional
          />
        </div>

        {/* ── Dates ── */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-axis-ink mb-2">
              Portfolio as-on date <span className="text-xs text-axis-ash font-normal">(optional)</span>
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-axis-cloud-200 rounded-md focus:ring-2 focus:ring-axis-maroon focus:outline-none"
              value={portfolioAsOn}
              onChange={(e) => setPortfolioAsOn(e.target.value)}
            />
            <div className="text-[11px] text-axis-ash mt-1 leading-snug">
              Auto-read from the Excel metadata if left blank.
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-axis-ink mb-2">
              DP statement as-on date <span className="text-xs text-axis-ash font-normal">(optional)</span>
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-axis-cloud-200 rounded-md focus:ring-2 focus:ring-axis-maroon focus:outline-none"
              value={dpAsOn}
              onChange={(e) => setDpAsOn(e.target.value)}
            />
            <div className="text-[11px] text-axis-ash mt-1 leading-snug">
              Auto-read from the DP PDF header if left blank.
            </div>
          </div>
        </div>

        {policyWarning && (
          <div className="flex gap-2 p-3 bg-amber-500/10 border border-amber-400/30 rounded-md text-amber-300 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <div>
              <div className="font-medium">Snapshot drift warning</div>
              <div>Portfolio and DP dates are more than 7 days apart. Mismatches from settlement drift are expected and will be flagged with the relevant root-cause code.</div>
            </div>
          </div>
        )}

        {mutation.isError && (
          <div className="flex gap-2 p-3 bg-rose-500/10 border border-rose-400/30 rounded-md text-rose-300 text-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <div>{(mutation.error as Error).message}</div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-axis-cloud-200">
          <button type="button" className="btn-secondary" onClick={() => nav('/recon')}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            disabled={!ready || mutation.isPending}
            onClick={() => {
              if (portfolioFiles.length > 0) {
                mutation.mutate({
                  portfolio_file: portfolioFiles[0],
                  portfolio_files: portfolioFiles,
                  dp_files: dpFiles,
                  portfolio_as_on: portfolioAsOn,
                  dp_as_on: dpAsOn,
                  pf_holding_file: pfHoldingFile ?? undefined,
                  dp_holding_file: dpHoldingFile ?? undefined,
                });
              }
            }}
          >
            <Upload className="w-4 h-4" />
            {mutation.isPending ? 'Uploading…' : 'Start reconciliation'}
          </button>
        </div>
      </div>
    </div>
  );
}
